import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo } from 'react';

interface NumberedPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Dòng tóm tắt bên trái, vd "Tổng: 42 proxy". */
  summary?: React.ReactNode;
  /** Số ô số hiển thị. */
  visiblePages?: number;
}

/** Số ô số mặc định — lẻ để trang hiện tại nằm chính giữa. */
const DEFAULT_VISIBLE_PAGES = 5;

/** Phân trang có ô số — dùng cho bảng dài. */
export function NumberedPagination({
  page,
  totalPages,
  onPageChange,
  summary,
  visiblePages: windowSize = DEFAULT_VISIBLE_PAGES,
}: NumberedPaginationProps) {
  // Trượt cửa sổ để trang hiện tại nằm giữa, nhưng không tràn khỏi [1, totalPages].
  const pages = useMemo(() => {
    if (totalPages <= 1) return [];
    const start = Math.max(
      1,
      Math.min(page - Math.floor(windowSize / 2), totalPages - windowSize + 1),
    );
    return Array.from({ length: Math.min(windowSize, totalPages) }, (_, i) => start + i).filter(
      (p) => p <= totalPages,
    );
  }, [page, totalPages, windowSize]);

  const arrowClass =
    'flex items-center gap-1 rounded-lg border border-line bg-canvas px-3 py-1.5 text-[12px] font-bold text-ink-muted transition-colors hover:bg-panel-hover hover:text-ink disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="border-line flex items-center justify-between border-t px-5 py-3">
      <span className="text-ink-dim text-[12px]">{summary}</span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => {
            onPageChange(page - 1);
          }}
          disabled={page <= 1}
          className={arrowClass}
        >
          <ChevronLeft size={14} />
          <span className="hidden sm:inline">Trước</span>
        </button>

        {pages.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => {
              onPageChange(p);
            }}
            className={`min-w-[32px] rounded-lg px-2.5 py-1.5 text-[12px] font-bold transition-colors ${
              p === page
                ? 'bg-accent text-white'
                : 'border-line bg-canvas text-ink-muted hover:bg-panel-hover hover:text-ink border'
            }`}
          >
            {p}
          </button>
        ))}

        <button
          type="button"
          onClick={() => {
            onPageChange(page + 1);
          }}
          disabled={page >= totalPages}
          className={arrowClass}
        >
          <span className="hidden sm:inline">Sau</span>
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
